import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { FaPlay, FaServer, FaSearch, FaSpinner, FaExpand, FaArrowLeft } from 'react-icons/fa';
import CustomPlayer from './components/CustomPlayer';
import ServerSelector from './components/ServerSelector';
import { API_BASE_URL } from './config';
import './App.css';

const API_BASE = API_BASE_URL;

function App() {
  const [currentView, setCurrentView] = useState('home'); // 'home' or 'fullscreen'
  const [urlTmdbId, setUrlTmdbId] = useState(null); // Store TMDB ID from URL
  const [isScrapingInProgress, setIsScrapingInProgress] = useState(false); // Prevent concurrent scraping
  const [tmdbId, setTmdbId] = useState('533535'); // Default movie: Deadpool & Wolverine
  const [contentType, setContentType] = useState('movie');
  const [season, setSeason] = useState('1');
  const [episode, setEpisode] = useState('1');
  const [sources, setSources] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedSource, setSelectedSource] = useState({ 
    id: 'hexa', 
    name: 'Hexa Stream',
    isDefault: true 
  }); // Pre-select default source for autoplay compatibility
  const [streamUrl, setStreamUrl] = useState('');
  const [tmdbData, setTmdbData] = useState(null);
  const [selectedServer, setSelectedServer] = useState('all'); // Default to All Servers (Auto-Failover)
  const [availableServers, setAvailableServers] = useState([]);
  const previousTmdbId = useRef(''); // Track previous TMDB ID with no default

  // URL Detection and Routing Logic
  useEffect(() => {
    const checkUrl = () => {
      const path = window.location.pathname;
      const movieMatch = path.match(/^\/movie\/(\d+)$/i);
      const tvMatch = path.match(/^\/tv\/(\d+)\/(\d+)\/(\d+)$/i); // /tv/tmdbId/season/episode
      
      // Check if URL has uppercase and redirect to lowercase
      if (path.includes('/MOVIE/') || path.includes('/TV/')) {
        const correctedPath = path.toLowerCase();
        window.history.replaceState({}, '', correctedPath);
        return; // Let the next check handle the corrected URL
      }
      
      if (movieMatch) {
        const movieId = movieMatch[1];
        console.log('🎬 Detected movie URL:', movieId);
        setUrlTmdbId(movieId);
        setTmdbId(movieId);
        setContentType('movie');
        setCurrentView('fullscreen');
        console.log('✅ Movie loaded from URL, ready for manual scraping');
      } else if (tvMatch) {
        const [, tvId, seasonNum, episodeNum] = tvMatch;
        console.log('📺 Detected TV URL:', tvId, `S${seasonNum}E${episodeNum}`);
        setUrlTmdbId(tvId);
        setTmdbId(tvId);
        setContentType('tv');
        setSeason(seasonNum);
        setEpisode(episodeNum);
        setCurrentView('fullscreen');
        console.log('✅ TV episode loaded from URL, ready for manual scraping');
      } else {
        // Home page
        setCurrentView('home');
        setUrlTmdbId(null);
      }
    };

    // Check URL on component mount
    checkUrl();

    // Listen for URL changes (back/forward browser buttons)
    const handlePopState = () => {
      checkUrl();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Fetch available servers on component mount
  useEffect(() => {
    const fetchServers = async () => {
      try {
        const response = await axios.get(`${API_BASE}/servers`);
        setAvailableServers(response.data.servers);
        console.log('🖥️ Available servers:', response.data.servers);
      } catch (error) {
        console.error('❌ Failed to fetch servers:', error);
        toast.error('Failed to load servers');
      }
    };

    fetchServers();
  }, []);

  // Function to navigate programmatically
  const navigateToMovie = (movieId) => {
    const newUrl = `/movie/${movieId}`;
    window.history.pushState({}, '', newUrl);
    setUrlTmdbId(movieId);
    setTmdbId(movieId);
    setContentType('movie');
    setCurrentView('fullscreen');
  };

  const navigateToTv = (tvId, seasonNum, episodeNum) => {
    const newUrl = `/tv/${tvId}/${seasonNum}/${episodeNum}`;
    window.history.pushState({}, '', newUrl);
    setUrlTmdbId(tvId);
    setTmdbId(tvId);
    setContentType('tv');
    setSeason(seasonNum);
    setEpisode(episodeNum);
    setCurrentView('fullscreen');
  };

  const navigateToHome = () => {
    window.history.pushState({}, '', '/');
    setCurrentView('home');
    setUrlTmdbId(null);
  };

  // Load available sources on mount
  useEffect(() => {
    const initializeApp = async () => {
      await loadSources();
      // Sources loaded, ready for manual scraping
      console.log('✅ App initialized, sources loaded');
    };
    
    initializeApp();
  }, []);

  const loadSources = async () => {
    try {
      console.log('🔍 Loading sources from:', `${API_BASE}/sources`);
      console.log('🔍 API_BASE value:', API_BASE);
      const response = await axios.get(`${API_BASE}/sources`);
      console.log('✅ Sources response:', response.data);
      setSources(response.data.sources);
    } catch (error) {
      console.error('❌ Failed to load sources:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      toast.error('Failed to load sources');
    }
  };

  const handleScrape = async (isAutoScrape = false, movieIdOverride = null, contentTypeOverride = null, seasonOverride = null, episodeOverride = null) => {
    const currentTmdbId = movieIdOverride || tmdbId;
    const currentContentType = contentTypeOverride || contentType;
    const currentSeason = seasonOverride || season;
    const currentEpisode = episodeOverride || episode;
    
    if (!currentTmdbId.trim()) {
      toast.error('Please enter a TMDB ID');
      return;
    }

    // Prevent concurrent scraping operations
    if (isScrapingInProgress) {
      console.log('🚫 Scraping already in progress, skipping...');
      return;
    }

    setIsScrapingInProgress(true);
    setLoading(true);
    setResults({});
    setStreamUrl('');

    try {
      // First, try to get TMDB data quickly to show hero image
      console.log('🎬 Fetching TMDB data for hero display...');
      try {
        const tmdbResponse = await axios.get(`${API_BASE}/tmdb/${currentTmdbId}?type=${currentContentType}`);
        if (tmdbResponse.data.success) {
          console.log('✅ TMDB data fetched for hero:', tmdbResponse.data.data?.title || tmdbResponse.data.data?.name);
          setTmdbData(tmdbResponse.data.data);
        }
      } catch (tmdbError) {
        console.log('⚠️ Could not fetch TMDB data for hero display:', tmdbError.message);
      }

      const payload = {
        tmdb_id: currentTmdbId,
        content_type: currentContentType,
        server: selectedServer,
        ...(currentContentType === 'tv' && { season: currentSeason, episode: currentEpisode })
      };

      console.log('🔍 Scrape payload:', payload); // Debug log
      const response = await axios.post(`${API_BASE}/scrape`, payload);
      
      if (response.data.success) {
        setResults(response.data.results);
        console.log('🎬 App: Setting TMDB data:', response.data.tmdb_data?.title || 'No title');
        setTmdbData(response.data.tmdb_data); // Store TMDB metadata
        if (!isAutoScrape) {
          toast.success(response.data.message);
        }
        
        // Auto-select first working source
        const workingSource = Object.entries(response.data.results).find(
          ([_, result]) => result.success
        );
        
        console.log('🔍 All results:', response.data.results);
        console.log('🎯 Working source found:', workingSource);
        
        if (workingSource) {
          handleSourceSelect(workingSource[0], workingSource[1]);
        } else {
          console.log('❌ No working source found');
        }
      } else {
        if (!isAutoScrape) {
          toast.error('No working streams found');
        }
      }
    } catch (error) {
      if (!isAutoScrape) {
        toast.error('Error scraping streams: ' + error.message);
      }
    } finally {
      setLoading(false);
      setIsScrapingInProgress(false); // Reset scraping flag
    }
  };

  const handleSourceSelect = (sourceId, result) => {
    console.log('🎯 Source selected:', sourceId, result);
    setSelectedSource({ id: sourceId, ...result });
    // Check for both 'url' and 'm3u8_url' properties
    const streamUrl = result.url || result.m3u8_url;
    if (streamUrl) {
      console.log('🎬 Setting stream URL:', streamUrl);
      setStreamUrl(streamUrl);
      toast.success(`Playing from ${sourceId}`);
    } else {
      console.error('❌ No URL found in result:', result);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleScrape();
    }
  };

  // Home View
  const renderHomeView = () => (
    <div className="App">
      <Toaster position="top-right" />
      
      <header className="App-header">
        <h1>🎬 Stream Scraper</h1>
        <p>Fast M3U8 extraction from multiple sources</p>
      </header>

      <div className="container">
        {/* Input Section */}
        <div className="input-section">
          <div className="input-group">
            <label>TMDB ID:</label>
            <input
              type="text"
              value={tmdbId}
              onChange={(e) => setTmdbId(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter TMDB ID"
              className="tmdb-input"
            />
          </div>

          <div className="input-group">
            <label>Content Type:</label>
            <select 
              value={contentType} 
              onChange={(e) => setContentType(e.target.value)}
              className="content-select"
            >
              <option value="movie">Movie</option>
              <option value="tv">TV Show</option>
            </select>
          </div>

          <div className="input-group">
            <label>Server:</label>
            <select 
              value={selectedServer} 
              onChange={(e) => setSelectedServer(e.target.value)}
              className="server-select"
            >
              {availableServers.map(server => (
                <option key={server.id} value={server.id}>
                  {server.name}
                </option>
              ))}
            </select>
          </div>

          {contentType === 'tv' && (
            <div className="tv-inputs">
              <div className="input-group">
                <label>Season:</label>
                <input
                  type="number"
                  value={season}
                  onChange={(e) => setSeason(e.target.value)}
                  min="1"
                  className="season-input"
                />
              </div>
              <div className="input-group">
                <label>Episode:</label>
                <input
                  type="number"
                  value={episode}
                  onChange={(e) => setEpisode(e.target.value)}
                  min="1"
                  className="episode-input"
                />
              </div>
            </div>
          )}

          {/* Show scrape button when TMDB ID is entered */}
          {tmdbId.trim() && (
            <button 
              onClick={handleScrape} 
              disabled={loading}
              className="scrape-button"
            >
              {loading ? (
                <>
                  <FaSpinner className="spinner" />
                  Scraping...
                </>
              ) : (
                <>
                  <FaSearch />
                  Scrape Streams
                </>
              )}
            </button>
          )}
        </div>

        {/* Results Section */}
        {Object.keys(results).length > 0 && (
          <div className="results-section" style={{ display: 'none' }}>
            <h3>Available Servers:</h3>
            <ServerSelector
              results={results}
              selectedSource={selectedSource}
              onSourceSelect={handleSourceSelect}
            />
          </div>
        )}

        {/* Video Player - Always Visible */}
        <div className="player-section">
          {/* Fullscreen Button */}
          {streamUrl && streamUrl !== 'loading' && tmdbId && (
            <div className="player-controls">
              <button 
                onClick={() => {
                  if (contentType === 'tv') {
                    navigateToTv(tmdbId, season, episode);
                  } else {
                    navigateToMovie(tmdbId);
                  }
                }}
                className="fullscreen-button"
                title="Open in fullscreen"
              >
                <FaExpand />
                Fullscreen
              </button>
            </div>
          )}
          
          {/* Video Player Component - Always Visible */}
          <div className="video-container">
            {(tmdbId.trim() && (loading || streamUrl)) ? (
              <CustomPlayer 
                src={streamUrl && streamUrl !== 'loading' ? streamUrl : ""} 
                tmdbData={tmdbData}
                mediaId={urlTmdbId || tmdbId}
                mediaType={contentType}
                title={
                  contentType === 'tv' 
                    ? `${tmdbData?.name || 'TV Show'} S${season}E${episode}` 
                    : (tmdbData?.title || tmdbData?.name || "Stream")
                }
                autoplay={streamUrl && streamUrl !== 'loading'}
                loading={loading || streamUrl === 'loading'}
              />
            ) : (
              <div className="video-placeholder">
                <div className="placeholder-content">
                  <FaSearch className="placeholder-icon" />
                  <p>Enter a TMDB ID and scrape to load a video stream</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sources Info */}
        <div className="sources-info">
          <h4>Available Sources ({sources.length}):</h4>
          <div className="sources-grid">
            {sources.map((source) => (
              <div key={source.id} className="source-card">
                <FaServer />
                <span>{source.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Fullscreen Player View
  const renderFullscreenPlayer = () => (
    <div className="fullscreen-player-view">
      {/* Fullscreen Player */}
      <div className="fullscreen-player-container">
        {streamUrl && streamUrl !== 'loading' ? (
          <CustomPlayer 
            src={streamUrl}
            tmdbData={tmdbData}
            mediaId={urlTmdbId || tmdbId}
            mediaType={contentType}
            title={
              contentType === 'tv' 
                ? `${tmdbData?.name || 'TV Show'} S${season}E${episode}` 
                : (tmdbData?.title || `Movie ${urlTmdbId || tmdbId}`)
            }
            autoplay={true}
            fullscreen={true}
          />
        ) : (streamUrl === 'loading' || loading) ? (
          <div className="loading-screen">
            <div className="loading-content">
              <div className="loading-spinner"></div>
              
            </div>
          </div>
        ) : (
          <div className="no-stream">
            <p>No stream available for this {contentType === 'tv' ? 'episode' : 'movie'}</p>
            <button onClick={navigateToHome} className="back-home-button">
              <FaArrowLeft />
              Back to Home
            </button>
          </div>
        )}
      </div>

      {/* Content Info Overlay */}
      {tmdbData && (
        <div className="movie-info-overlay">
          <h1>
            {contentType === 'tv' 
              ? `${tmdbData.name} S${season}E${episode}` 
              : tmdbData.title
            }
          </h1>
          <p>{tmdbData.overview}</p>
        </div>
      )}
    </div>
  );

  // Main render logic
  if (currentView === 'fullscreen') {
    return (
      <div className="App">
        {renderFullscreenPlayer()}
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
      </div>
    );
  }

  // Default home view
  return (
    <div className="App">
      {renderHomeView()}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
    </div>
  );
}

export default App;
